import { useCallback, useEffect, useState } from 'react'
import NProgress from 'nprogress'
import { useTranslation } from 'react-i18next'
import { useToaster } from 'hooks/useToaster'
import * as yup from 'yup'
import { Form, useForm, ValidationSchema } from 'components/Form/Form'
import { FormInput } from 'components/Form/FormInput'
import { Button } from 'components/Button'
import { GoogleButton, GoogleButtonSuccessHandler } from 'components/auth/GoogleButton'
import { useAuth } from 'contexts/auth-context'
import { Input } from 'components/Input'
import { RequestFreeTrialReqDto } from 'api/models'
import { usePreDefinedCompanyName } from 'hooks/usePreDefinedCompanyName'
import { WrongTeamToken } from 'components/free-trial/WrongTeamToken'
import { TermsAndConditions } from 'components/free-trial/TermsAndConditions'

interface RequestFreeTrialFormProps {
  onGoogleSuccess: GoogleButtonSuccessHandler
  teamToken?: string
}

export const RequestFreeTrialForm = ({ onGoogleSuccess, teamToken }: RequestFreeTrialFormProps) => {
  const [completed, setCompleted] = useState(false)
  const { preDefinedCompanyName, isTokenCheckFailed } = usePreDefinedCompanyName(teamToken)
  const { t } = useTranslation()
  const toaster = useToaster()
  const { requestFreeTrial } = useAuth()
  const formContext = useForm<SignUpFormValues>({ validationSchema: signUpSchema })

  const handleSubmit = useCallback(
    (values: SignUpFormValues) => {
      NProgress.start()
      const requestData: RequestFreeTrialReqDto =
        teamToken != null ? { ...values, teamToken } : values
      requestFreeTrial(requestData)
        .then(() => setCompleted(true))
        .catch((error) => toaster.error(error))
        .finally(() => NProgress.done())
    },
    [teamToken, requestFreeTrial, toaster],
  )

  useEffect(() => {
    if (preDefinedCompanyName == null) {
      return
    }
    formContext.setValue('companyName', preDefinedCompanyName)
  }, [formContext, preDefinedCompanyName])

  const handleSubmitInvalid = useCallback(() => {
    toaster.error('auth.freeTrial.signUp.errors.checkFields')
  }, [toaster])

  if (isTokenCheckFailed) {
    return <WrongTeamToken />
  }

  if (completed) {
    return (
      <div className="flex flex-col items-center">
        <h3 className="text-normal mb-[16px] font-medium text-gray-normal">
          {t('auth.freeTrial.trialRequested.title')}
        </h3>
        <p className="text-normal text-gray-normal mb-[32px]">
          {t('auth.freeTrial.trialRequested.subTitle')}
        </p>
      </div>
    )
  }

  return (
    <>
      <div className="text-center mb-[48px]">
        <h2 className="text-header-small mb-[8px]">{t('auth.freeTrial.signUp.title')}</h2>
        <h3 className="text-normal text-gray-normal">{t('auth.freeTrial.signUp.subTitle')}</h3>
      </div>
      <div className="max-w-[320px] mx-auto">
        <Form<SignUpFormValues>
          formContext={formContext}
          onSubmit={handleSubmit}
          onSubmitInvalid={handleSubmitInvalid}
        >
          <FormInput<SignUpFormValues>
            name="name"
            autoComplete="given-name"
            containerClassName="mb-[24px]"
            className="bg-gray-inputBG"
            placeholder={t('auth.freeTrial.signUp.firstName')}
          />
          <FormInput<SignUpFormValues>
            name="lastName"
            autoComplete="family-name"
            containerClassName="mb-[24px]"
            className="bg-gray-inputBG"
            placeholder={t('auth.freeTrial.signUp.lastName')}
          />
          <FormInput<SignUpFormValues>
            name="email"
            autoComplete="email"
            containerClassName="mb-[24px]"
            className="bg-gray-inputBG"
            placeholder={t('auth.freeTrial.signUp.email')}
          />
          {preDefinedCompanyName != null ? (
            <Input
              name="companyName"
              containerClassName="mb-[24px]"
              className="bg-gray-inputBG"
              placeholder={t('auth.freeTrial.signUp.company')}
              value={preDefinedCompanyName}
              title={t('auth.freeTrial.signUp.preDefinedCompanyWarning')}
              disabled
            />
          ) : (
            <FormInput<SignUpFormValues>
              name="companyName"
              containerClassName="mb-[24px]"
              className="bg-gray-inputBG"
              placeholder={t('auth.freeTrial.signUp.company')}
            />
          )}
          <TermsAndConditions />
          <Button
            className="w-full"
            isSmall
            type="submit"
            disabled={formContext.formState.isValidating || !formContext.formState.isValid}
          >
            {t('auth.freeTrial.signUp.button')}
          </Button>
          <GoogleButton className="mt-[24px]" onSuccess={onGoogleSuccess}>
            {t('auth.common.signUpWithGoogle')}
          </GoogleButton>
        </Form>
      </div>
    </>
  )
}

interface SignUpFormValues {
  name: string
  lastName: string
  email: string
  companyName: string
}

export const signUpSchema = yup.object<ValidationSchema<SignUpFormValues>>({
  name: yup.string().required(),
  lastName: yup.string().required(),
  email: yup.string().email().required(),
  companyName: yup.string().required(),
})
